// components/Layout/Layout.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Menu, LayoutDashboard, User, LogOut, ChevronDown,
  Bell, Settings,
} from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { useTheme } from '../../hooks/useTheme';
import Sidebar from '../Sidebar';
import LanguageSwitcher from '../LanguageSwitcher';
import AiAssistantWidget from '../AiAssistant/AiAssistantWidget';
import { BRAND_NAME } from '../../config/env';

// ─── Composant TopBar (commun desktop + mobile) ───────────────────────────────
const TopBar: React.FC = () => {
  const { user, logout } = useAuth();
  const { sidebarOpen, setSidebarOpen } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fermer le dropdown si clic à l'extérieur
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isOnDashboard = location.pathname === '/dashboard' || location.pathname === '/';
  const userName = user ? `${user.prenom || ''} ${user.nom || ''}`.trim() || user.email : 'Utilisateur';
  const userInitials = user
    ? `${user.prenom?.[0] || ''}${user.nom?.[0] || ''}`.toUpperCase() || user.email?.[0]?.toUpperCase() || '?'
    : '?';

  const handleLogout = () => {
    setDropdownOpen(false);
    logout();
    navigate('/login');
  };

  return (
    // Le bandeau prend la couleur SECONDAIRE de l'entite — le second ton de
    // son logo. La couleur du texte est calculee selon la luminance du fond,
    // de sorte qu'un bleu nuit comme celui d'AGEROUTE recoive du texte clair
    // sans que personne ait a le prevoir.
    <header
      className="print:hidden flex items-center justify-between px-4 py-2.5 border-b shadow-sm flex-shrink-0 z-30 gap-3"
      style={{
        backgroundColor: 'var(--color-header, #ffffff)',
        color: 'var(--color-header-text, #1f2937)',
      }}
    >

      {/* ── Gauche : burger (mobile) + logo + bouton Dashboard ── */}
      <div className="flex items-center gap-2 min-w-0">
        {/* Burger mobile */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-1.5 rounded-lg hover:bg-[var(--color-header-hover)] [color:var(--color-header-muted)] lg:hidden flex-shrink-0"
          aria-label="Menu"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Nom de l'entite. Le logo n'est plus repete ici : il figure deja en
            tete de la barre laterale, et deux exemplaires cote a cote sur la
            meme ligne d'ecran encombraient le bandeau sans rien apprendre. */}
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="font-bold text-sm [color:var(--color-header-text)] truncate hidden sm:block"
            style={{ color: 'var(--color-primary, #1a56db)' }}
          >
            {user?.entiteNom ?? BRAND_NAME}
          </span>
        </div>

        {/* Séparateur */}
        {!isOnDashboard && (
          <span className="hidden lg:block [color:var(--color-header-muted)] select-none mx-1">|</span>
        )}

        {/* Bouton retour au Dashboard */}
        {!isOnDashboard && (
          <Link
            to="/dashboard"
            className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors [color:var(--color-header-muted)] hover:text-blue-600 hover:bg-[var(--color-header-hover)]"
            title={t('topbar.dashboard')}
          >
            <LayoutDashboard className="w-3.5 h-3.5" />
            {t('topbar.dashboard')}
          </Link>
        )}
      </div>

      {/* ── Droite : notifications + profil utilisateur ── */}
      <div className="flex items-center gap-1 flex-shrink-0">

        {/* Bouton retour Dashboard (mobile, icône seule) */}
        {!isOnDashboard && (
          <Link
            to="/dashboard"
            className="lg:hidden p-1.5 rounded-lg hover:bg-[var(--color-header-hover)] [color:var(--color-header-muted)] hover:text-blue-600"
            title={t('topbar.dashboard')}
          >
            <LayoutDashboard className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
          </Link>
        )}

        {/* Sélecteur de langue */}
        <LanguageSwitcher />

        {/* Cloche notifications */}
        <button
          onClick={() => navigate('/alertes')}
          className="relative p-1.5 rounded-lg hover:bg-[var(--color-header-hover)] [color:var(--color-header-muted)] hover:[color:var(--color-header-text)]"
          title={t('topbar.alerts')}
        >
          <Bell className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
        </button>

        {/* ── Profil utilisateur ── */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[var(--color-header-hover)] transition-colors"
          >
            {/* Avatar */}
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm"
              style={{ background: `linear-gradient(135deg, var(--color-primary, #1a56db), var(--color-accent, #f59e0b))` }}
            >
              {userInitials}
            </div>
            {/* Nom + rôle (desktop) */}
            <div className="hidden md:block text-left leading-tight">
              <p className="text-sm font-semibold [color:var(--color-header-text)] truncate max-w-[130px]">{userName}</p>
              <p className="text-xs [color:var(--color-header-muted)] capitalize truncate max-w-[130px]">
                {user?.profilNom || user?.role || 'Utilisateur'}
              </p>
            </div>
            <ChevronDown
              className={`w-3.5 h-3.5 [color:var(--color-header-muted)] transition-transform hidden md:block ${dropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Panneau BLANC : ne pas y utiliser les variables --color-header-* — calculées pour
              le fond SOMBRE de la barre, elles donnent du texte blanc sur blanc. */}
          {/* ── Dropdown menu ── */}
          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
              {/* Header dropdown */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow"
                    style={{ background: `linear-gradient(135deg, var(--color-primary, #1a56db), var(--color-accent, #f59e0b))` }}
                  >
                    {userInitials}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{userName}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    <span
                      className="inline-block mt-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full text-white"
                      style={{ background: 'var(--color-primary, #1a56db)' }}
                    >
                      {user?.profilNom || user?.role}
                    </span>
                  </div>
                </div>
              </div>

              {/* Mon espace / profil */}
              <button
                onClick={() => { setDropdownOpen(false); navigate('/profil'); }}
                className="w-full text-left px-4 py-2.5 text-sm text-gray-800 hover:bg-gray-50 flex items-center gap-2.5 transition-colors"
              >
                <User className="w-4 h-4 text-gray-500" />
                {t('topbar.myProfile')}
              </button>

              {/* Tableau de bord */}
              <button
                onClick={() => { setDropdownOpen(false); navigate('/dashboard'); }}
                className="w-full text-left px-4 py-2.5 text-sm text-gray-800 hover:bg-gray-50 flex items-center gap-2.5 transition-colors"
              >
                <LayoutDashboard className="w-4 h-4 text-gray-500" />
                {t('topbar.dashboard')}
              </button>

              <hr className="my-1 border-gray-100" />

              {/* Déconnexion */}
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2.5 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                {t('topbar.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

// ─── Layout principal ──────────────────────────────────────────────────────────
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, permissionsLoaded, user } = useAuth();
  const { t } = useTranslation();

  // Injecter la charte graphique de l'entité dès que l'utilisateur est chargé
  useTheme(user ?? null);

  if (loading || !permissionsLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loadingApp')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <React.Suspense fallback={<div className="min-h-screen" />}>
        {children}
      </React.Suspense>
    );
  }

  return (
    <div id="app-layout" className="flex h-screen bg-gray-50">
      <Sidebar />

      {/* Zone principale */}
      <div id="app-content-wrapper" className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* ── TopBar (visible sur toutes les tailles d'écran) ── */}
        <TopBar />

        {/* Bandeau intérim */}
        {Array.isArray((user as any)?.profilsInterim) && (user as any).profilsInterim.length > 0 && (
          <div className="print:hidden flex items-center gap-2 px-4 py-2 bg-orange-500 text-white text-sm font-medium flex-shrink-0">
            <span>&#9888;</span>
            <span>Vous assurez des fonctions d'intérim — droits temporaires actifs</span>
          </div>
        )}

        {/* Contenu principal.

            La frontiere de suspension est ICI, et non autour de l'arbre de
            routes : les pages etant chargees a la demande, un fallback place
            plus haut remplacerait tout l'ecran — barre laterale et bandeau
            compris — a chaque navigation, d'ou l'effet de clignotement. En le
            confinant au conteneur central, le cadre reste fige et seule la
            zone de contenu change. */}
        <main id="app-main" className="flex-1 overflow-auto p-3 sm:p-4 lg:p-4">
          <React.Suspense
            fallback={
              <div className="flex items-center justify-center py-24 text-gray-400 text-sm">
                Chargement…
              </div>
            }
          >
            {children}
          </React.Suspense>
        </main>
      </div>

      {/* ── Assistant IA conversationnel (FAB global) ── */}
      <AiAssistantWidget />
    </div>
  );
};

export default Layout;
